/* eslint-disable complexity */
import React, { useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { Box } from 'carbon-react/lib/components/box/box.component';
import Button from 'carbon-react/lib/components/button/button.component';
import Typography from 'carbon-react/lib/components/typography';

import { getOnboardingInitialRoute, Logger } from '@futrli/shared-services';
import { ListingPageHelpLink } from '@futrli/ui-components/components/listing-page/listing-page-help-link/listing-page-help-link';
import { OrganisationOverviewView } from '@futrli/views/OrganisationOverviewView';
import { toastError } from '@futrli/ui-components';

import { CustomersSuppliersCardsRow } from './customers-suppliers-cards-row';
import { NewInsightsEmailSidebar } from '../email-insights/new-email-insights-sidebar';
import { Option, PeriodOption } from '../../types';
import { PerformanceCardsRow } from './performance-cards-row';
import { PeriodToggle } from './period-toggle';
import { PRIOR_PERIOD_VALUE, ShowPerformanceSelect } from './show-performance-select';
import { useIsExcelOrganisation } from '../../../predict-management/hooks/useIsExcelOrganisation';

import * as styles from './performance-overview.scss';

interface PerformanceOverviewProps {
  view: OrganisationOverviewView;
}

export const PerformanceOverview = ({ view }: PerformanceOverviewProps) => {
  const intl = useIntl();
  const isExcelOrganisation = useIsExcelOrganisation();
  const [isOpenInsightsEmail, setIsOpenInsightsEmail] = useState(false);

  const { workspaceId = '', organisationId = '' } = useParams();

  const {
    comparisonMode,
    customersSuppliersInfo,
    errorSavingPreferences,
    errorSavingSchedule,
    forecasts,
    isComparisonLoading,
    isInitialLoading,
    activeSchedule,
    selectedForecast,
    schedules,
    period: periodValue,
    periodData,
    performanceToTargetsPeriodData
  } = view.state;

  useEffect(() => {
    if (errorSavingPreferences) {
      Logger.error(errorSavingPreferences.message);
      toastError();
    }
  }, [errorSavingPreferences]);

  useEffect(() => {
    if (errorSavingSchedule) {
      Logger.error(errorSavingSchedule.message);
      toastError();
    }
  }, [errorSavingSchedule]);

  const periodOptions: Option[] = useMemo(
    () => [
      {
        name: intl.formatMessage({ id: 'reporting.period.this_month', defaultMessage: 'This month' }),
        value: PeriodOption.THIS_MONTH
      },
      {
        name: intl.formatMessage({ id: 'reporting.period.this_quarter', defaultMessage: 'This quarter' }),
        value: PeriodOption.THIS_QUARTER
      },
      {
        name: intl.formatMessage({ id: 'reporting.period.this_fin_year', defaultMessage: 'This financial year' }),
        value: PeriodOption.THIS_YEAR
      }
    ],
    [intl]
  );

  const period: Option = useMemo(() => periodOptions.find(o => o.value === periodValue) || periodOptions[0], [
    periodOptions,
    periodValue
  ]);

  const handlePeriodChange = (selectedValue: string) => {
    view.setPeriod(selectedValue);
  };

  const handleComparisonChange = (value: string) => {
    view.setComparisonForecast(value);
  };

  const helpLinkTo = getOnboardingInitialRoute(workspaceId, organisationId, isExcelOrganisation ? 'excel' : undefined);

  return (
    <>
      <div className={styles.container}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Box display="flex" gap="16px" alignItems="center">
            <Typography variant="h1" fontSize="20px" fontWeight="500" lineHeight="140%" mb={0}>
              <FormattedMessage id="reporting.performance" defaultMessage="Performance" />
            </Typography>
            <ListingPageHelpLink url="https://support.futrli.com/en/articles/14301065-performance" />
          </Box>

          <Button
            className={styles.emailButton}
            buttonType="primary"
            iconPosition="before"
            iconType="email"
            onClick={() => setIsOpenInsightsEmail(true)}
          >
            <FormattedMessage id="predict.performance_overview.email_insights" defaultMessage="Email insights" />
          </Button>
        </Box>

        <div className={styles.controlGroup} style={{ marginBottom: '24px' }}>
          <PeriodToggle options={periodOptions} selectedValue={period.value} onChange={handlePeriodChange} />
        </div>

        <Typography fontSize="14px" fontWeight="500" lineHeight="140%" mb={2}>
          <FormattedMessage
            id="predict.performance_overview_prior_period_total"
            defaultMessage="Performance against prior period's total"
          />
        </Typography>

        <PerformanceCardsRow periodData={periodData} comparisonMode={PRIOR_PERIOD_VALUE} isLoading={isInitialLoading} />

        <ShowPerformanceSelect
          selectedValue={comparisonMode}
          forecasts={forecasts}
          onChange={handleComparisonChange}
          helpLinkTo={helpLinkTo}
        />

        <PerformanceCardsRow
          periodData={performanceToTargetsPeriodData}
          comparisonMode={comparisonMode}
          isLoading={isComparisonLoading || isInitialLoading}
        />

        <div style={{ borderBottom: '1px solid #CCD6DB', marginBottom: '32px' }} />

        {!isExcelOrganisation && (
          <>
            <Typography fontSize="16px" fontWeight="500" lineHeight="160%" mb={1} mt={4}>
              <FormattedMessage id="predict.customers_and_suppliers" defaultMessage="Customers & Suppliers" />
            </Typography>
            <CustomersSuppliersCardsRow
              unpaidCashInFromInvoices={customersSuppliersInfo?.unpaidCashInFromInvoices}
              unpaidCashOutFromBills={customersSuppliersInfo?.unpaidCashOutFromBills}
              daysNumCustomersPaying={customersSuppliersInfo?.daysNumCustomersPaying}
              daysNumPayingToSuppliers={customersSuppliersInfo?.daysNumPayingToSuppliers}
              isLoading={isInitialLoading}
            />
          </>
        )}
      </div>

      {isOpenInsightsEmail && periodData && (
        <NewInsightsEmailSidebar
          view={view}
          hasSchedules={!!schedules?.length}
          selectedForecastId={selectedForecast?.id}
          activeSchedule={activeSchedule}
          isOpen={isOpenInsightsEmail}
          handleClose={() => setIsOpenInsightsEmail(false)}
          period={period}
          periodData={periodData}
          performanceToTargetsPeriodData={performanceToTargetsPeriodData}
          selectedForecast={selectedForecast}
          forecastsNotExist={forecasts.length === 0}
          customersSuppliersInfo={customersSuppliersInfo}
        />
      )}
    </>
  );
};
